import { Radio } from "@mui/material";
import { forwardRef, useImperativeHandle, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import swalDialog from "../../helpers/swalErrorPopup";
import CouponIcon from "../../img/svg/coupon.svg";
import MastercardNew from "../../img/svg/Mastercard_new.svg";
import { isLoadChanger } from "../../store/mainSlice";
import {
  activeMultipleEntryPassesDetailsSelector,
  canBuyNewMultipleEntryPassSelector,
  hasActiveMultipleEntryPassSelector,
  selectIsRegularMaofEntryPossible,
} from "../../store/multipleEntryPassSlice";

import userAPI from "../../api/userAPI";
import { isPartnerClalit, isPartnerOvdimBari, isPartnerMaoflaamit } from "../../store/partnerStore";

const MultipleEntryPassPaymentLabel = forwardRef(
  ({ last4Digit, selectedPaymentMethod, onPaymentMethodChange, onPassPlanChange }, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const mainUserParams = useSelector((state) => state.userSlice.mainUserData);
    const isClalit = useSelector(isPartnerClalit);
    const isOvdimBari = useSelector(isPartnerOvdimBari);
    const isMaoflaamit = useSelector(isPartnerMaoflaamit);

    const multipleEntryPassDetails = useSelector(activeMultipleEntryPassesDetailsSelector);
    const hasActiveMultipleEntryPass = useSelector(hasActiveMultipleEntryPassSelector);
    const canBuyNewMultipleEntryPass = useSelector(canBuyNewMultipleEntryPassSelector);
    const isRegularMaofEntryPossible = useSelector(selectIsRegularMaofEntryPossible);

    const [selectedPassPlanId, setSelectedPassPlanId] = useState(3);

    const handlePayment = async () => {
      if (hasActiveMultipleEntryPass) return;
      try {
        dispatch(isLoadChanger(true));
        const result = await userAPI.purchaseMultipleEntryPass({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
          passPlanId: isMaoflaamit ? selectedPassPlanId : isOvdimBari ? 2 : 1,
        });
        return result;
      } catch (error) {
        swalDialog({ error });
        throw error;
      } finally {
        dispatch(isLoadChanger(false));
      }
    };

    useImperativeHandle(ref, () => ({
      handlePayment,
    }));

    // Coupon row: Clalit/OvdimBari (always), Maoflaamit only when using active pass (not buying new)
    const showCouponRow =
      (isClalit || isOvdimBari || (isMaoflaamit && hasActiveMultipleEntryPass)) &&
      ((hasActiveMultipleEntryPass && multipleEntryPassDetails.NumberOfEntries < 4) ||
        (!hasActiveMultipleEntryPass && canBuyNewMultipleEntryPass && !isMaoflaamit));

    // Maoflaamit plan selection rows: shown when buying new pass, unless regular entry is possible
    const showMaoflaamitPlanRows =
      isMaoflaamit &&
      !hasActiveMultipleEntryPass &&
      canBuyNewMultipleEntryPass &&
      !isRegularMaofEntryPossible;

    return (
      <>
        {showCouponRow && (
          <div className="mobile-popup-payment-method-container">
            <div className="mobile-popup-card-number">
              <img src={CouponIcon} alt="Coupon" style={{ width: "28px", height: "28px" }} />
            </div>
            <div className="radio-btn-container">
              <p className={hasActiveMultipleEntryPass ? "credit-passes" : "credit-card"}>
                {isMaoflaamit
                  ? `כרטיסיית ${multipleEntryPassDetails?.PossibleNumberOfEntries} כניסות`
                  : t("MULTIPLE_ENTRY_PASS_DETAILS")}
                {hasActiveMultipleEntryPass && (
                  <span className="multiple-entry-pass__reminder">
                    {t("MULTIPLE_ENTRY_PASS_REMINDED", {
                      remaining:
                        multipleEntryPassDetails.PossibleNumberOfEntries - multipleEntryPassDetails.NumberOfEntries,
                    })}
                  </span>
                )}
              </p>
              <Radio
                checked={selectedPaymentMethod === "multipleEntryPass" || hasActiveMultipleEntryPass}
                onChange={() => onPaymentMethodChange && onPaymentMethodChange("multipleEntryPass")}
              />
            </div>
          </div>
        )}

        {showMaoflaamitPlanRows && (
          <>
            <div className="mobile-popup-payment-method-container">
              <div className="mobile-popup-card-number">
                <img src={CouponIcon} alt="Coupon" style={{ width: "28px", height: "28px" }} />
              </div>
              <div className="radio-btn-container">
                <p className="credit-card">כרטיסיית 3 כניסות</p>
                <Radio
                  checked={selectedPassPlanId === 3 && selectedPaymentMethod === "multipleEntryPass"}
                  onChange={() => {
                    setSelectedPassPlanId(3);
                    onPaymentMethodChange && onPaymentMethodChange("multipleEntryPass");
                    onPassPlanChange && onPassPlanChange(3);
                  }}
                />
              </div>
            </div>
            <div className="mobile-popup-payment-method-container">
              <div className="mobile-popup-card-number">
                <img src={CouponIcon} alt="Coupon" style={{ width: "28px", height: "28px" }} />
              </div>
              <div className="radio-btn-container">
                <p className="credit-card">כרטיסיית 4 כניסות</p>
                <Radio
                  checked={selectedPassPlanId === 4 && selectedPaymentMethod === "multipleEntryPass"}
                  onChange={() => {
                    setSelectedPassPlanId(4);
                    onPaymentMethodChange && onPaymentMethodChange("multipleEntryPass");
                    onPassPlanChange && onPassPlanChange(4);
                  }}
                />
              </div>
            </div>
          </>
        )}

        {last4Digit &&
          ((!isClalit && !isOvdimBari && !isMaoflaamit) ||
            ((isClalit || isOvdimBari) && !hasActiveMultipleEntryPass) ||
            (hasActiveMultipleEntryPass && multipleEntryPassDetails.NumberOfEntries >= 4) ||
            (isMaoflaamit &&
              !(hasActiveMultipleEntryPass && multipleEntryPassDetails.NumberOfEntries < 4) &&
              (isRegularMaofEntryPossible || (!canBuyNewMultipleEntryPass && isRegularMaofEntryPossible === false)))) && (
            <div className="mobile-popup-payment-method-container">
              <div className="mobile-popup-card-number">
                <span className="card-dots">●●●●</span>
                <span className="card-number">{last4Digit}</span>
                <img src={MastercardNew} alt="" className="card-logo" />
              </div>
              <div className="radio-btn-container">
                <p className="credit-card">{t("CREDIT_CARD")}</p>
                <Radio
                  checked={selectedPaymentMethod === "creditCard"}
                  onChange={() => onPaymentMethodChange && onPaymentMethodChange("creditCard")}
                />
              </div>
            </div>
          )}
      </>
    );
  },
);

export default MultipleEntryPassPaymentLabel;
